import MDTypography from "components/MDTypography";
import colors from "assets/theme-dark/base/colors";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer as MuiTableContainer,
  TableHead,
  TableRow,
  Backdrop,
  CircularProgress,
  IconButton,
  styled,
  Tooltip,
} from "@mui/material";
import {
  useQueryProcessedViewsDataLazyQuery,
  useQueryProcessedDataViewsQuery,
  useQueryProcessedTableMatcherTableNameLazyQuery,
} from "graphql/types/graphql";
import { useCallback, useContext, useEffect, useState } from "react";
import { context } from "utils/Provider";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MyTable from "./components/Table";
import Pagination from "components/Pagination";
import { PageData } from "@/hooks/usePagination";
import { useDeleteProcessedData } from "./hooks/useDeleteProcessedData";
import DeleteConfirmationDialog from "./components/DeleteConfirmationDialog";

interface Column {
  id: "id" | "name" | "createdAt" | "actions";
  label: string;
  sortable?: boolean;
}

const columns: Column[] = [
  {
    id: "name",
    label: "Table Name",
    sortable: true,
  },
  {
    id: "createdAt",
    label: "Created At",
    sortable: true,
  },
  {
    id: "actions",
    label: "Actions",
  },
];

const emptyTableData = {
  queryProcessedViewsData: {
    __typename: "ProcessedDataResponse" as const,
    edges: [] as any[],
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    success: true,
    error: null as string | null,
  },
};

function AllTables(): JSX.Element {
  const { setIsLoading, selectedCube } = useContext(context);
  const [tableView, setTableView] = useState<boolean>(false);
  const [selectedTableName, setSelectedTableName] = useState<string>("");
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSizeDataTable, setPageSizeDataTable] = useState(10);
  const [currentPageDataTable, setCurrentPageDataTable] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [tableToDelete, setTableToDelete] = useState<string | null>(null);
  const [sortField, setSortField] = useState<"name" | "createdAt">("createdAt");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");

  const { deleteTable, loading: deleteLoading, error: deleteError } = useDeleteProcessedData();

  const { data, loading, error, refetch } = useQueryProcessedDataViewsQuery({
    variables: {
      cubeId: selectedCube.cubeId,
      limit: pageSize,
      offset: currentPage * pageSize,
    },
  });

  const [
    getViewData,
    { error: tableDataError, data: tableData, loading: tableDataLoading, refetch: refetchTableData },
  ] = useQueryProcessedViewsDataLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const [getTableMatcherData, { error: matcherError, data: matcherData, loading: matcherLoading }] =
    useQueryProcessedTableMatcherTableNameLazyQuery({
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    setTableView(false);
    setSelectedTableName("");
    setCurrentPage(0);
    setCurrentPageDataTable(0);
    setSortField("createdAt");
    setSortDirection("DESC");
  }, [selectedCube.cubeId]);

  const getViewDataHandler = useCallback(
    (viewName: string) => {
      getViewData({
        variables: {
          cubeId: selectedCube.cubeId,
          filter: {
            viewName: viewName,
          },
          limit: pageSizeDataTable,
          offset: currentPageDataTable * pageSizeDataTable,
        },
      });
    },
    [getViewData, selectedCube, pageSizeDataTable, currentPageDataTable]
  );

  useEffect(() => {
    if (selectedTableName && tableView) {
      getTableMatcherData({
        variables: {
          tableName: selectedTableName,
        },
      });
    }
  }, [selectedTableName, tableView, getTableMatcherData]);

  useEffect(() => {
    if (matcherData?.queryProcessedTableMatcherTableName?.success) {
      setIdentifier(matcherData.queryProcessedTableMatcherTableName.identifier || null);
    } else if (matcherData?.queryProcessedTableMatcherTableName?.error) {
      console.error(matcherData.queryProcessedTableMatcherTableName.error);
      setIdentifier(null);
    }
  }, [matcherData]);

  useEffect(() => {
    if (tableView && selectedTableName) {
      getViewDataHandler(selectedTableName);
    }
  }, [currentPageDataTable, getViewDataHandler, pageSizeDataTable, selectedTableName, tableView]);

  useEffect(() => {
    setIsLoading(loading || matcherLoading || deleteLoading);
  }, [loading, matcherLoading, deleteLoading, setIsLoading]);

  useEffect(() => {
    setIsLoading(tableDataLoading);
  }, [setIsLoading, tableDataLoading]);

  useEffect(() => {
    setCurrentPage(0);
  }, [pageSize, sortField, sortDirection]);

  const handleOpenDeleteDialog = (tableName: string) => {
    setTableToDelete(tableName);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTableToDelete(null);
  };

  const handleDelete = async () => {
    if (tableToDelete) {
      const result = await deleteTable(tableToDelete);
      if (result.success) {
        refetch();
      }
      handleCloseDeleteDialog();
    }
  };

  const handleSort = (field: "name" | "createdAt") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortDirection("ASC");
    }
  };

  const sortedEdges = [...(data?.queryProcessedDataViews?.edges || [])].sort((a, b) => {
    if (sortField === "name") {
      return sortDirection === "ASC" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    }
    if (sortField === "createdAt") {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortDirection === "ASC" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  if (error || matcherError || deleteError) {
    return <MDTypography>Error: {String(error || matcherError || deleteError)}</MDTypography>;
  }

  if (data === undefined && !loading) {
    return (
      <MDTypography
        sx={{
          fontWeight: "600",
          color: colors.text.main,
          textAlign: "center",
        }}
      >
        No tables found
      </MDTypography>
    );
  }

  const paginationData: PageData = {
    pageSize: pageSize,
    page: currentPage,
    count: data?.queryProcessedDataViews?.totalCount ?? 0,
  };

  if (tableView) {
    return (
      <MDBox
        sx={{
          marginTop: "10px",
          borderRadius: "4px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: "1rem",
          background: colors.background.default,
        }}
      >
        <MDBox>
          <MDButton onClick={() => setTableView(false)} variant="text">
            <ChevronLeftIcon sx={{ width: "2rem", height: "2rem" }} />
          </MDButton>
        </MDBox>
        <MyTable
          identifier={identifier}
          tableName={selectedTableName}
          pageSize={pageSizeDataTable}
          setPageSize={setPageSizeDataTable}
          currentPage={currentPageDataTable}
          setCurrentPage={setCurrentPageDataTable}
          data={tableData || emptyTableData}
          loading={tableDataLoading}
        />
      </MDBox>
    );
  }

  return (
    <>
      <MDBox
        sx={{
          marginTop: "10px",
          borderRadius: "4px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          padding: "1rem",
          background: colors.background.default,
        }}
      >
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: 999999 }} open={Boolean(loading)}>
            <CircularProgress color="success" />
          </Backdrop>
        )}
        <TableContainer>
          <StyledTable stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    align="center"
                    key={column.id}
                    onClick={column.sortable ? () => handleSort(column.id as "name" | "createdAt") : undefined}
                    sx={{
                      cursor: column.sortable ? "pointer" : "default",
                      "&:hover": column.sortable ? { backgroundColor: "rgba(0, 0, 0, 0.08)" } : {},
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      color="text"
                      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      {column.label}
                      {column.sortable &&
                        sortField === column.id &&
                        (sortDirection === "ASC" ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        ))}
                    </MDTypography>
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <StyledBodyTableCell colSpan={columns.length} align="center">
                    <CircularProgress size={20} />
                  </StyledBodyTableCell>
                </TableRow>
              ) : sortedEdges.length > 0 ? (
                sortedEdges.map((row: any, index: number) => (
                  <TableRow
                    sx={{
                      "&:hover": {
                        cursor: "pointer",
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                      "&:hover .delete-icon": {
                        visibility: "visible",
                      },
                    }}
                    key={index}
                  >
                    <StyledBodyTableCell
                      align="center"
                      onClick={() => {
                        setSelectedTableName(row.name);
                        setTableView(true);
                      }}
                    >
                      <MDTypography
                        sx={{
                          color: "primary.main",
                          fontWeight: "bold",
                        }}
                      >
                        {row.name}
                      </MDTypography>
                    </StyledBodyTableCell>
                    <StyledBodyTableCell
                      align="center"
                      onClick={() => {
                        setSelectedTableName(row.name);
                        setTableView(true);
                      }}
                    >
                      <MDTypography variant="caption" color="text">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
                      </MDTypography>
                    </StyledBodyTableCell>
                    <StyledBodyTableCell align="center">
                      <Tooltip title="Delete Table">
                        <IconButton
                          size="small"
                          className="delete-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(row.name);
                          }}
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    </StyledBodyTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledBodyTableCell colSpan={columns.length} align="center">
                    <MDTypography variant="caption" color="text">
                      No data found!
                    </MDTypography>
                  </StyledBodyTableCell>
                </TableRow>
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </MDBox>
      <MDBox>
        <Pagination changePage={setCurrentPage} changePageSize={setPageSize} pageData={paginationData} />
      </MDBox>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        tableName={tableToDelete}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDelete}
        deleteLoading={deleteLoading}
      />
    </>
  );
}

const TableContainer = styled(MuiTableContainer)`
  overflow-x: auto;
  background: ${colors.background.default};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const StyledTable = styled(MuiTable)`
  min-width: 400px;
  border-radius: 16px;
`;

const StyledTableCell = styled(TableCell)`
  background: ${colors.background.card};
  color: ${colors.text.main};
  border-bottom: 1px solid ${colors.grey[800]};
  padding: 8px;
  position: sticky;
  top: 0;
`;

const StyledBodyTableCell = styled(TableCell)`
  color: ${colors.text.main};
  border-bottom: 1px solid ${colors.grey[300]};
  padding: 8px;
`;

export default AllTables;
